// src/components/Pentests/runs/SubdomainView.js
//
// Subdomain enumeration result list. Each row is `result.value` (the FQDN);
// `result.data.ip` is the resolved IP if present.

import { useMemo, useState } from 'react';
import { Globe, Search } from 'lucide-react';

export default function SubdomainView({ results }) {
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return results;
    return results.filter((r) => {
      const ip = (r.data && (r.data.ip || r.data.address)) || '';
      return r.value.toLowerCase().includes(q) || String(ip).toLowerCase().includes(q);
    });
  }, [results, filter]);

  if (results.length === 0) {
    return (
      <EmptyHint icon={Globe}>No subdomains discovered yet.</EmptyHint>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter subdomains or IPs..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70"
          />
        </div>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {filtered.length === results.length
            ? `${results.length} subdomains`
            : `${filtered.length} of ${results.length}`}
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
        <table className="w-full text-sm text-left">
          <thead className="bg-cyber-200/60 text-xs uppercase tracking-wider text-slate-700">
            <tr>
              <th className="px-4 py-3">Subdomain</th>
              <th className="px-4 py-3">Resolved IP</th>
              <th className="px-4 py-3">Source</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const ip = (r.data && (r.data.ip || r.data.address)) || '';
              const source = (r.data && r.data.source) || '';
              return (
                <tr key={r.id} className="border-t border-cyber-300/30 hover:bg-cyber-200/40">
                  <td className="px-4 py-2 font-mono text-shinobi-green break-all">{r.value}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-700">{ip || '-'}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{source || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyHint({ icon: I, children }) {
  return (
    <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-10 text-center text-slate-500">
      <I className="w-6 h-6 mx-auto mb-2 opacity-60" />
      {children}
    </div>
  );
}
